<template>
    <div class="at-card">
        <div class="at-header">
            <i class="fal fa-history at-header-icon"></i>
            <h3 class="at-title">{{ $t('user_profile.activity') }}</h3>
            <span class="at-count" v-if="histories.length">
                {{ histories.length }}
            </span>
        </div>
        <div v-if="histories.length === 0" class="at-empty">
            <i class="fal fa-inbox"></i>
            <span>{{ $t('user_profile.no_activity') }}</span>
        </div>
        <div v-else class="at-timeline">
            <div
                v-for="(entry, index) in histories.slice(0, displayCount)"
                :key="index"
                class="at-item"
            >
                <div class="at-dot" :class="'at-dot--' + (entry.entity || '').toLowerCase()"></div>
                <div class="at-content">
                    <div class="at-row">
                        <span class="at-action">
                            <i :class="getEntityIcon(entry.entity)" class="at-action-icon"></i>
                            {{ getEntityLabel(entry) }}
                        </span>
                        <span class="at-date">
                            {{ moment(entry.created_at || entry.updated_at).fromNow() }}
                        </span>
                    </div>
                    <div class="at-post" v-if="entry.entity_id">
                        <router-link :to="`/posts/${entry.entity_id}`" class="at-post-link">
                            <span class="at-post-ref">Ticket #{{ entry.entity_id }}</span>
                        </router-link>
                    </div>

                    <!-- Status change -->
                    <div v-if="entry.entity === 'Status' && entry.old_value && entry.new_value" class="at-detail">
                        <span class="at-badge at-badge--old">{{ entry.old_value.name || entry.old_value.status }}</span>
                        <i class="fal fa-arrow-right at-arrow"></i>
                        <span class="at-badge at-badge--new">{{ entry.new_value.name || entry.new_value.status }}</span>
                    </div>

                    <!-- Priority change -->
                    <div v-if="entry.entity === 'Priority' && entry.new_value" class="at-meta">
                        {{ $t('user_profile.priority_set') }}
                        <strong>{{ entry.new_value.name || entry.new_value }}</strong>
                    </div>

                    <!-- Operator change -->
                    <div v-if="entry.entity === 'Operator' && entry.new_value" class="at-meta">
                        {{ $t('user_profile.operator_assigned') }}
                        <strong>{{ typeof entry.new_value === 'string' ? entry.new_value : entry.new_value.name }}</strong>
                    </div>
                </div>
            </div>
            <div v-if="histories.length > displayCount" class="at-more">
                <button @click="displayCount += 10" class="at-btn-more">
                    <i class="fal fa-chevron-down"></i>
                    {{ $t('my_profile.see_all_activity') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
const ENTITY_ICONS = {
    Post: 'fal fa-ticket',
    Status: 'fal fa-exchange-alt',
    Operator: 'fal fa-user-check',
    Comment: 'fal fa-sticky-note',
    Message: 'fal fa-comment-alt',
    Priority: 'fal fa-flag'
}

export default {
    name: 'ActivityTimeline',
    props: {
        histories: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            displayCount: 10
        }
    },
    methods: {
        getEntityIcon(entity) {
            return ENTITY_ICONS[entity] || 'fal fa-circle'
        },
        getEntityLabel(entry) {
            const entity = (entry.entity || '').toLowerCase()
            const action = entry.action || ''
            const key = `user_profile.entities.${entity}.${action}`
            const translated = this.$t(key)
            return translated !== key ? translated : `${entry.entity} - ${action}`
        }
    }
}
</script>

<style scoped>
.at-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.at-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 24px;
    border-bottom: 1px solid #f1f5f9;
}

.at-header-icon {
    font-size: 1rem;
    color: #3b82f6;
    flex-shrink: 0;
}

.at-title {
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
}

.at-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 7px;
    border-radius: 12px;
    background: #0891B2;
    color: #fff;
    font-size: 0.68rem;
    font-weight: 700;
    margin-left: auto;
}

.at-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 40px 24px;
    color: #94a3b8;
    font-size: 0.85rem;
}

.at-empty i {
    font-size: 1.5rem;
}

/* Timeline */
.at-timeline {
    padding: 12px 16px;
}

.at-item {
    display: flex;
    gap: 10px;
    padding-bottom: 12px;
    position: relative;
}

.at-item:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 16px;
    bottom: 0;
    width: 2px;
    background: #e2e8f0;
}

.at-item:last-child {
    padding-bottom: 0;
}

.at-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #94a3b8;
    flex-shrink: 0;
    margin-top: 4px;
}

.at-dot--post { background: #10b981; }
.at-dot--status { background: #f59e0b; }
.at-dot--operator { background: #0891B2; }
.at-dot--comment { background: #8b5cf6; }
.at-dot--message { background: #3b82f6; }
.at-dot--priority { background: #ef4444; }

.at-content {
    flex: 1;
    min-width: 0;
}

.at-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    margin-bottom: 4px;
}

.at-action {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 0.75rem;
    font-weight: 700;
    color: #0f172a;
}

.at-action-icon {
    font-size: 0.68rem;
    color: #64748b;
}

.at-date {
    font-size: 0.65rem;
    color: #94a3b8;
    white-space: nowrap;
}

.at-post {
    margin-bottom: 2px;
}

.at-post-link {
    text-decoration: none;
}

.at-post-ref {
    font-size: 0.68rem;
    font-weight: 700;
    color: #0891B2;
    background: rgba(8, 145, 178, 0.08);
    padding: 2px 6px;
    border-radius: 4px;
}

.at-post-link:hover .at-post-ref {
    background: rgba(8, 145, 178, 0.15);
}

/* Detail: status change */
.at-detail {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
}

.at-badge {
    font-size: 0.72rem;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 6px;
}

.at-badge--old {
    background: #fef2f2;
    color: #dc2626;
    text-decoration: line-through;
}

.at-badge--new {
    background: #ecfdf5;
    color: #059669;
}

.at-arrow {
    font-size: 0.6rem;
    color: #cbd5e1;
}

/* Meta info */
.at-meta {
    margin-top: 4px;
    font-size: 0.75rem;
    color: #64748b;
}

.at-meta strong {
    color: #334155;
}

/* Load more */
.at-more {
    padding-top: 8px;
    border-top: 1px solid #f1f5f9;
}

.at-btn-more {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    padding: 8px;
    border-radius: 8px;
    background: #f8fafc;
    border: 1.5px solid #e2e8f0;
    color: #475569;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.at-btn-more:hover {
    border-color: #0891B2;
    color: #0891B2;
    background: rgba(8, 145, 178, 0.04);
}

.at-btn-more i {
    font-size: 0.65rem;
}
</style>
